import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, } from '@angular/core';
import moment from 'moment';
import { SpeechToTextService } from '@privatelibdev/speech-to-text';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { Router } from '@angular/router';
import { ROUTE_CONSTANTS } from '../../util/route.constants';

import { AppointmentResponse, IAppointment } from "../../models/appointment.interface";
import { TimeDifferencePipe } from "../../shared/pipes/time-difference.pipe";
import { ActionClick, GridColumn, RowClick } from '../../models/data-table.interface';
import { environment } from '../../../environments/environment';
import { AppointmentService } from '../../services/appointment/appointment.service';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AgePipe } from "../../shared/pipes/age.pipe";
import { PractitionerService } from '../../services/practitioner/practitioner.service';
import { SidenavService } from '../../services/sidenav/sidenav.service';
import { AppointmentStatusEnum, CVA_CONSTANTS, MANUAL_REFRESH_IDENTIFIER, VIEW_MODE } from '../../util/constants';
import { AdditionalData, ManualResponse } from '../../models/manual-refresh.interface';
import { CVA_AUTH_MODULES } from '../../models/auth.modules.enum';
import { SoapServiceService } from "../../services/soap-service/soap-service.service";
import { ConfirmationDialogService } from '../../shared/confirmation-dialog/services/confirmation-dialog.service';
import { lastValueFrom } from 'rxjs';
import { ISelectedAppointment } from '../../models/storage.interface';
import { MatDialog } from '@angular/material/dialog';
import { TranscriptionModalComponent } from './transcription-modal/transcription-modal.component';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { TasksService } from '../../services/tasks/tasks.service';

@Component({
    standalone: false,
    selector: 'cva-appointment-list',
    templateUrl: './appointment-list.component.html',
    styleUrl: './appointment-list.component.scss',
    
})
export class AppointmentListComponent implements OnInit, AfterViewInit {
  @Input() isCompactMode: boolean = false;
  @Input() isDashboardSelectionMode: boolean = false;
  @Input() allowPastTranscriptionView: boolean = false;
  @Input() externalBookingOpen: boolean = false;
  @Output() patientSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() bookingPanelStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  practitionerId!: string;
  appointments!: IAppointment[];
  walkInAppointments: IAppointment[] = [];
  filteredAppointments!: IAppointment[];
  filteredWalkInAppointments!: IAppointment[];
  showPagination = true;
  bookAppointmentMode = false;
  isRefreshing: boolean = false;
  isCurrentDay: boolean = true;
  isAppointmentsLoading: boolean = false;
  private appointmentsLoadingCount = 0;
  @ViewChild(MatTabGroup, { static: false }) tabGroup!: MatTabGroup;

  deleteConfirmationMessage: string = CVA_CONSTANTS.MESSAGE.DELETE_CONFIRMATION_MESSAGE;
  cvaUI_url: string = environment.cvaUI_url;

  displayedColumns: GridColumn<IAppointment>[] = [
    { field: "time", label: "Time", tdClass: "apt-time" },
    { field: "name", label: "Patient Name", width: "15%", tdClass: "patient-name" },
    { field: "patientGender", label: "Gender", tdClass: "normal-text" },
    { field: "patientAge", label: "Age", tdClass: "normal-text" },
    { field: "appointmentType", label: "Visit Type", tdClass: "normal-text" },
    { field: "notes", label: "Reason", width: "15%", tdClass: "notes-field normal-text text-break", hasLargeText: true },
    { field: "status", label: "Status", tdClass: "status-field normal-text" },
    { field: "duration", label: "Duration", tdClass: "normal-text" },
    {
      field: "hasTranscription",
      label: "Transcription",
      tdClass: "normal-text",
      eventType: 'transcription',
      icon: this.cvaUI_url + "/assets/icons/notes.svg",
      disabledIcon: this.cvaUI_url + "/assets/icons/notes-disabled.svg",
      readOnlyIcon: this.cvaUI_url + "/assets/icons/notes-readonly.svg"
    },
  ];
  Tab = {
    MY_APPOINTMENT: 0,
    WALK_IN: 1
  };
  selectedIndex: number = 0;
  selectedDate = moment(new Date()).startOf('D').toDate();
  today = new Date();
  isVisible: boolean = false;

  //for filtering the data
  appointmentFilterString: string = "";
  isVoiceInputActive = false;

  manualRefreshIdentifier: MANUAL_REFRESH_IDENTIFIER =
    MANUAL_REFRESH_IDENTIFIER.PRACTITIONER_APPOINTMENT;
  additionalParams!: AdditionalData;
  CVA_AUTH_MODULES = CVA_AUTH_MODULES;
  CVA_CONSTANTS = CVA_CONSTANTS;
  dateMode: 'today' | 'tomorrow' | 'dayAfter' | '' = 'today';


  constructor(
    private readonly cvaNotificationService: CVANotificationService,
    private readonly speechToTextService: SpeechToTextService,
    private readonly router: Router,
    private readonly apiService: AppointmentService,
    private readonly timeDifferencePipe: TimeDifferencePipe,
    private readonly datePipe: DatePipe,
    private readonly agePipe: AgePipe,
    private readonly practitionerService: PractitionerService,
    private readonly sideNavService: SidenavService,
    private readonly soapServiceService: SoapServiceService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly dialog: MatDialog,
    private readonly tasksService: TasksService
  ) {
    const providerId = localStorage.getItem('providerId');
    this.practitionerId = providerId ?? '';
  }

  ngOnInit() {
    console.log("Appointment dashboard loaded, fetching practitioner details");
    // Hide pagination in compact mode
    this.showPagination = !this.isCompactMode;
    this.practitionerService.getPractitionerByHpiId(this.practitionerId).subscribe(() => {
      console.log("Practitioner details fetched");
      this.fetchAppointments();
      this.fetchWalkinAppointment();
    }, () => {
      this.isAppointmentsLoading = false;
    });
    this.setManualRefreshAdditionalParams();
  }

  private startAppointmentsLoading(): void {
    this.appointmentsLoadingCount += 1;
    this.isAppointmentsLoading = true;
  }

  private stopAppointmentsLoading(): void {
    this.appointmentsLoadingCount = Math.max(0, this.appointmentsLoadingCount - 1);
    this.isAppointmentsLoading = this.appointmentsLoadingCount > 0;
  }

  ngAfterViewInit() {
    this.tabGroup.selectedIndex = this.selectedIndex;
    setTimeout(() => {
      this.isVisible = true;
    }, 250);
  }

  onTabChanged(event: MatTabChangeEvent) {
    this.selectedIndex = event.index;
    this.fetchAppointments();
  }

  resetSelectedTab() {
    if (this.tabGroup) {
      this.tabGroup.selectedIndex = 0;
    }
  }

  selectToday() {
    if (this.dateMode !== 'today') {
      this.resetSelectedTab();
      this.selectedDate = moment().startOf('d').toDate();
      this.fetchAppointments();
      this.isCurrentDay = true;
    }
  }

  selectTomorrow() {
    if (this.dateMode !== 'tomorrow') {
      this.resetSelectedTab();
      this.selectedDate = moment().startOf('d').add(1, 'd').toDate();
      this.fetchAppointments();
      this.isCurrentDay = false;
    }
  }

  selectDayAfter() {
    if (this.dateMode !== 'dayAfter') {
      this.resetSelectedTab();
      this.selectedDate = moment().startOf('d').add(2, 'd').toDate();
      this.fetchAppointments();
      this.isCurrentDay = false;
    }
  }

  goToPreviousDay() {
    this.resetSelectedTab();
    this.selectedDate = moment(this.selectedDate).subtract(1, 'd').startOf('d').toDate();
    this.fetchAppointments();
    this.isCurrentDay = moment(this.selectedDate).isSame(new Date(), 'd');
  }

  goToNextDay() {
    this.resetSelectedTab();
    this.selectedDate = moment(this.selectedDate).add(1, 'd').startOf('d').toDate();
    this.fetchAppointments();
    this.isCurrentDay = moment(this.selectedDate).isSame(new Date(), 'd');
  }

  openBookAppointment() {
    console.log("Open book appointment window");
    if (this.isCompactMode) {
      this.bookingPanelStateChange.emit(true);
      return;
    }
    this.bookAppointmentMode = true;
    this.bookingPanelStateChange.emit(true);
  }

  onCloseBookingSection() {
    console.log("Close book appointment window");
    if (this.isCompactMode) {
      this.bookingPanelStateChange.emit(false);
      return;
    }
    this.bookAppointmentMode = false;
    this.bookingPanelStateChange.emit(false);
  }

  openAppointment(record: RowClick<IAppointment>, viewMode = VIEW_MODE.TAB) {
    if (!record.rowData.isDeleted) {
      if (!this.isCurrentDay) {
        this.cvaNotificationService.showNotification(
          CVA_CONSTANTS.MESSAGE.CONSULTATION_ALLOWED_ONLY_FOR_TODAY,
          NotificationClassEnum.ERROR
        );
        return;
      }
      if (record.rowData.pastAppointment) {
        this.cvaNotificationService.showNotification(
          'Consultation not allowed for past appointments.',
          NotificationClassEnum.ERROR
        );
        return;
      }
      const appointment = record.rowData;
      if (this.isDashboardSelectionMode) {
        this.patientSelected.emit(appointment.patient.medicalRecordNumber);
        return;
      }
      const savedConsultation = this.soapServiceService.getAppointmentAndDraftedNotes();
      this.soapServiceService.setCurrentAppointment(appointment);
      this.handleAppointmentNavigation(appointment, savedConsultation, viewMode).then(() => { });
    }
  }

  async handleAppointmentNavigation(
    appointment: IAppointment,
    savedConsultation: ISelectedAppointment | null,
    viewMode: VIEW_MODE) {

    //when no consultation is saved, save and navigate
    if (!savedConsultation?.referenceId) {
      return await this.handleNotesConfirmation(appointment, viewMode);
    }

    //get the stored appointment
    const storedAppointment = [...this.appointments, ...this.walkInAppointments]
      .find(appointment => appointment.referenceId === savedConsultation.referenceId);
    if (storedAppointment) {
      //when different appointment found, prompt a message
      const hasDifferentAppointment = savedConsultation.referenceId !== appointment.referenceId;
      if (hasDifferentAppointment) {
        if (savedConsultation.transcription.length !== 0) {
          //check for pending notes & its confirmation
          this.handlePendingConsultationConfirmation(storedAppointment, appointment, viewMode);
        } else {
          //check for notes confirmation
          await this.handleNotesConfirmation(appointment, viewMode);
        }
      } else {
        //do not show any prompt and do not save again
        this.saveAndNavigateAppointmentIfNeeded(appointment, viewMode);
      }
    } else {
      await this.handleNotesConfirmation(appointment, viewMode)
    }
  }


  async warningDialog(message: string): Promise<boolean | undefined> {
    const confirmed$ = this.confirmationDialogService.openConfirmDialog(message);
    return await lastValueFrom(confirmed$);
  }

  async handlePendingConsultationConfirmation(
    storedAppointment: IAppointment,
    appointment: IAppointment,
    viewMode: VIEW_MODE
  ) {
    const isConfirmed = await this.warningDialog(CVA_CONSTANTS.MESSAGE.PENDING_NOTES_WARNING);

    if (isConfirmed === true) {
      await this.handleNotesConfirmation(appointment, viewMode);
    } else if (isConfirmed === false) {
      this.setPatientInfoAndNavigate(storedAppointment, viewMode);
    }
  }

  /**
   * handle notes confirmation
   * - when notes are approved & has no trancription present then only show the prompt message
   * - otherwise just save the appointment and navigate 
   * @param appointment 
   * @param viewMode 
   */
  async handleNotesConfirmation(appointment: IAppointment, viewMode: VIEW_MODE) {
    if (!appointment.isActive) {
      const isConfirmed = await this.warningDialog(CVA_CONSTANTS.MESSAGE.CONSULTATION_ALREADY_DONE);
      if (isConfirmed) {
        this.saveAndNavigateAppointmentIfNeeded(appointment, viewMode, true);
      }
    } else {
      this.saveAndNavigateAppointmentIfNeeded(appointment, viewMode, true);
    }
  }

  saveAndNavigateAppointmentIfNeeded(appointment: IAppointment, viewMode = VIEW_MODE.TAB, isToBeSaved = false) {
    if (isToBeSaved) {
      this.deleteTabs();
      this.soapServiceService.setAppointmentAndDraftedNotes(appointment.referenceId, {}, appointment.transcription, '', appointment.tabsData);
    }
    this.setPatientInfoAndNavigate(appointment, viewMode);
  }

  deleteTabs() {
    localStorage.removeItem("tabs");
    localStorage.removeItem("consultationForm");
    localStorage.removeItem("disableAllApproves");
    this.tasksService.clearAll();
  }

  setPatientInfoAndNavigate(appointment: IAppointment, viewMode = VIEW_MODE.TAB) {
    console.log("Open appointment for consultation");
    this.sideNavService.patientId$ = appointment.patient.referenceId;
    this.sideNavService.patientMRN$ = appointment.patient.medicalRecordNumber;
    this.router.navigate([ROUTE_CONSTANTS.CONSULTATION], { state: { appointment, viewMode, practitioner: this.practitionerService.loggedInPractitioner } });
  }

  onVoiceInput() {
    this.isVoiceInputActive = true;
    this.speechToTextService
      .recognizeOnce()
      .then((text: string | null) => {
        if (text) {
          this.appointmentFilterString = text.replaceAll('.', '');
          this.filterAppointments();
        }
        return this.speechToTextService.stopRecording();
      })
      .catch((err) => {
        if (err.message) {
          this.cvaNotificationService.showNotification(
            err.message,
            NotificationClassEnum.ERROR
          );
        }
      }).finally(() => {
        this.isVoiceInputActive = false;
      });
  }

  filterAppointments() {
    const query = this.appointmentFilterString.trimStart().toLowerCase();

    const filterByName = (appointments: IAppointment[]) =>
      appointments.filter((appointment) => {
        const fullName = `${appointment.patient.firstName} ${appointment.patient.lastName}`.toLowerCase();
        return fullName.includes(query);
      });

    if (this.selectedIndex === this.Tab.MY_APPOINTMENT) {
      this.filteredAppointments = filterByName(this.appointments);
    } else {
      this.filteredWalkInAppointments = filterByName(this.walkInAppointments);
    }
  }

  filterByDateTime(appointments: IAppointment[]) {
    return appointments.sort((a, b) => a.start.localeCompare(b.start));
  }

  onDateSelect(value: Date) {
    if (value) {
      this.fetchAppointments();
      this.isCurrentDay = moment(this.selectedDate).isSame(new Date(), 'd');
    }
  }

  onActionClick(record: ActionClick<IAppointment>): void {
    switch (record.action) {
      case "delete":
        if (!record.rowData.isDeleted && !record.rowData.pastAppointment) {
          this.cancelAppointment(
            record.rowData.uid,
            record.rowData.start,
            record.rowData.referenceId
          );
        }
        break;
      case "transcription":
        if (record.rowData.hasTranscription) {
          if (record.rowData.pastAppointment) {
            const transcriptionDialogRef = this.dialog.open(TranscriptionModalComponent, {
              data: record.rowData
            });
            transcriptionDialogRef.afterClosed().subscribe((refresh: boolean) => {
              if (refresh) {
                this.fetchAppointments();
              }
            });
          } else {
            this.openAppointment(record, VIEW_MODE.TAB);
          }
        }
        break;
      default:
        break;
    }
  }

  canViewTranscription(appointment: IAppointment): boolean {
    if (!appointment.hasTranscription) {
      return false;
    }

    if (appointment.pastAppointment) {
      return this.allowPastTranscriptionView;
    }

    return true;
  }

  getTranscriptionTooltip(appointment: IAppointment): string {
    if (!appointment.hasTranscription) {
      return 'No transcription available';
    }

    if (this.allowPastTranscriptionView) {
      return (appointment.transcription || '').trim() || 'No transcription available';
    }

    if (appointment.pastAppointment && !this.allowPastTranscriptionView) {
      return 'Transcription unavailable for past appointments';
    }

    return 'View Transcription';
  }

  getTranscriptionIcon(appointment: IAppointment): string {
    if (!appointment.hasTranscription) {
      return this.cvaUI_url + '/assets/icons/notes-disabled.svg';
    }

    if (appointment.pastAppointment) {
      return this.allowPastTranscriptionView
        ? this.cvaUI_url + '/assets/icons/notes-readonly.svg'
        : this.cvaUI_url + '/assets/icons/notes-disabled.svg';
    }

    return this.cvaUI_url + '/assets/icons/notes.svg';
  }

  setAppointmentRecords(records: AppointmentResponse[]) {
    const appointments: IAppointment[] = records.map((appointment) => {
      return {
        ...appointment,
        time: this.datePipe.transform(appointment.start, 'HH:mm') ?? '',
        name: `${appointment.patient?.firstName} ${appointment.patient?.lastName}`,
        patientGender: appointment.patient.gender,
        patientAge: this.agePipe.transform(appointment.patient.dateOfBirth),
        duration: this.timeDifferencePipe.transform(appointment.start, appointment.end),
        isDeleted: appointment.status === AppointmentStatusEnum.CANCELLED,
        hasTranscription: (appointment.transcription && appointment.transcription.length > 0) || false,
        pastAppointment: moment().startOf('d').diff(moment(appointment.start).startOf('d'), 'd') > 0
      };
    });
    return this.filterByDateTime(appointments);
  }

  fetchAppointments() {
    const daysDifference = moment().startOf('d').diff(this.selectedDate, 'd');
    switch (daysDifference) {
      case 0:
        this.dateMode = 'today';
        break;
      case -1:
        this.dateMode = 'tomorrow';
        break;
      case -2:
        this.dateMode = 'dayAfter';
        break;
      default:
        this.dateMode = '';
    }
    this.setManualRefreshAdditionalParams();
    if (this.selectedIndex === this.Tab.MY_APPOINTMENT) {
      this.fetchMyAppointment();
    } else {
      this.fetchWalkinAppointment();
    }
  }

  fetchMyAppointment() {
    console.log("Fetching appointments for logged in practitioner for selected date");
    this.startAppointmentsLoading();
    this.apiService.getAppointmentsByPractitionerId(
      this.practitionerId,
      this.selectedDate,
      moment(this.selectedDate).endOf('day').toDate(),
      false
    ).subscribe({
      next: (res) => {
        console.log("Appointments fetched successfully");
        this.appointments = this.setAppointmentRecords(res.response);
        this.filteredAppointments = JSON.parse(JSON.stringify(this.appointments));
        if (this.filteredAppointments.length === 0) {
          this.showPagination = false;
        } else {
          this.showPagination = true;
        }
        this.stopAppointmentsLoading();
      }, error: (errorResponse: HttpErrorResponse) => {
        console.log("Appointments fetch failure");
        let errorMessage: string = '';
        if (errorResponse.error?.error) {
          errorMessage = errorResponse.error.error.errorMessage;
        }
        this.cvaNotificationService.showNotification(
          errorMessage || CVA_CONSTANTS.MESSAGE.NO_APPOINTMENT,
          NotificationClassEnum.ERROR
        );
        this.stopAppointmentsLoading();
      }
    });
  }

  fetchWalkinAppointment() {
    console.log("Fetching appointments for walkins tab for selected date");
    this.startAppointmentsLoading();
    this.apiService.getAppointmentsByPractitionerId(
      this.practitionerId,
      this.selectedDate,
      moment(this.selectedDate).endOf('day').toDate(),
      true
    ).subscribe({
      next: (res) => {
        console.log("Appointments fetched successfully");
        this.walkInAppointments = this.setAppointmentRecords(res.response);
        this.filteredWalkInAppointments = JSON.parse(JSON.stringify(this.walkInAppointments));
        if (this.filteredWalkInAppointments.length === 0) {
          this.showPagination = false;
        } else {
          this.showPagination = true;
        }
        this.stopAppointmentsLoading();
      }, error: (errorResponse: HttpErrorResponse) => {
        console.log("Walkins fetch failure");
        let errorMessage: string = '';
        if (errorResponse.error?.error) {
          errorMessage = errorResponse.error.error.errorMessage;
        }
        this.cvaNotificationService.showNotification(
          errorMessage || CVA_CONSTANTS.MESSAGE.NO_WALK_IN_APPOINTMENT,
          NotificationClassEnum.ERROR
        );
        this.stopAppointmentsLoading();
      }
    });
  }

  cancelAppointment(uid: string, start: string, referenceId: string) {
    //verify the saved appointment
    const savedConsultation = this.soapServiceService.getAppointmentAndDraftedNotes();
    let message = CVA_CONSTANTS.MESSAGE.DELETE_CONFIRMATION_MESSAGE;
    let hasSavedAppointment = false;

    if (savedConsultation?.referenceId === referenceId &&
      Object.keys(savedConsultation.notes).length !== 0) {
      message = CVA_CONSTANTS.MESSAGE.CANCEL_APPOINTMENT_WITH_PENDING_NOTES_WARNING;
      hasSavedAppointment = true;
    }

    const confirmed$ = this.confirmationDialogService.openConfirmDialog(message);
    lastValueFrom(confirmed$).then((isConfirmed: boolean) => {
      if (isConfirmed === true) {
        if (moment().isAfter(moment(start))) {
          this.cvaNotificationService.showNotification(
            CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_THE_PAST_APPOINTMENT,
            NotificationClassEnum.ERROR
          );
          return;
        }
        console.log("Cancelling an appointment");
        this.apiService.cancelAppointmentById(uid).subscribe({
          next: () => {
            console.log("Appointment cancellation successful");
            this.markAppointmentAsDeleted(uid, this.appointments)
            this.markAppointmentAsDeleted(uid, this.filteredAppointments)

            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.APPOINTMENT_CANCELLED_SUCCESSFULLY,
              NotificationClassEnum.SUCCESS
            );

            //remove from storage
            if (hasSavedAppointment) {
              this.soapServiceService.removeAppointmentAndDraftedNotes();
            }
          },
          error: (errorResponse: HttpErrorResponse) => {
            console.log("Appointment cancellation failure");
            let errorMessage: string = '';
            if (errorResponse.error?.error) {
              errorMessage = errorResponse.error.error.errorMessage;
              if (errorMessage === CVA_CONSTANTS.MESSAGE.NO_RECORD_FOUND) {
                errorMessage = CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_THE_APPOINTMENT
              }
            }
            this.cvaNotificationService.showNotification(
              errorMessage || CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_THE_APPOINTMENT,
              NotificationClassEnum.ERROR
            );
          }
        });
      }
    });
  }

  markAppointmentAsDeleted(uid: string, appointments: IAppointment[]): void {
    const appointment = appointments.find(a => a.uid === uid);
    if (appointment) {
      appointment.isDeleted = true;
      appointment.status = AppointmentStatusEnum.CANCELLED;
    }
  }

  patchRefreshedData(data: ManualResponse<unknown>): void {
    if (data.identifier === this.manualRefreshIdentifier) {
      this.resetSelectedTab();
      this.appointments = this.setAppointmentRecords(data.records as IAppointment[]);
      this.filteredAppointments = JSON.parse(JSON.stringify(this.appointments))
      this.appointmentFilterString = '';
    }
  }

  /**
   * set additional params for manual refresh
   * -start & end need to be set
   */
  setManualRefreshAdditionalParams() {
    this.additionalParams = {
      start: moment(this.selectedDate).startOf('day').toISOString(),
      end: moment(this.selectedDate).endOf('day').toISOString(),
    } as AdditionalData;
  }

  onAppointmentSchedule(appointmentDate: Date) {
    if (moment(appointmentDate).isSame(moment(this.selectedDate), 'day')) {
      this.fetchAppointments();
    }
  }
}
